import { flattenTree, resolveColumnHeadersId } from '../utils/tree-utils';
import { getColumnsForLevel } from '../utils/column-config-utils';
import { parseFormula } from '../utils/formula-utils';
// ============================================================
// Public API
// ============================================================
/**
 * Compiles a tree of RowNodes into a flat array of RenderRows.
 *
 * This is the renderer-agnostic compilation step. Both the Excel
 * renderer and the web renderer consume this intermediate representation.
 */
export function compileReport(nodes, columnConfig, theme, dataContext) {
    const flat = flattenTree(nodes);
    const defaultColumns = columnConfig.default;
    const result = [];
    // Separate title_block nodes (rendered first, above everything)
    const titleBlockNodes = flat.filter((n) => n.data.type === 'title_block');
    const dataNonTitleNodes = flat.filter((n) => n.data.type !== 'title_block');
    // Check whether any column_headers rows exist on canvas
    const hasColumnHeaderRows = dataNonTitleNodes.some((n) => n.data.type === 'column_headers');
    // Helper: resolve columns for a data row via binding
    function getColumnsForDataRow(node) {
        const levelCols = getColumnsForLevel(columnConfig, node.depth);
        if (levelCols.length > 0)
            return levelCols;
        const boundHeaderId = resolveColumnHeadersId(node, nodes);
        if (boundHeaderId) {
            const headerNode = flat.find((n) => n.id === boundHeaderId);
            if (headerNode && headerNode.data.type === 'column_headers') {
                return headerNode.data.columns;
            }
        }
        return defaultColumns;
    }
    // ── Phase 1: Title blocks ──
    for (const node of titleBlockNodes) {
        result.push(compileTitleBlock(node, defaultColumns, theme));
    }
    // ── Phase 2: Default column headers (only if no column_headers rows on canvas) ──
    if (!hasColumnHeaderRows) {
        result.push(compileColumnHeaders(defaultColumns, theme));
    }
    // ── Phase 3: Data rows ──
    for (const node of dataNonTitleNodes) {
        if (node.data.type === 'column_headers') {
            const colHeaderData = node.data;
            result.push(compileColumnHeadersFromNode(node, colHeaderData, theme));
            continue;
        }
        const nodeColumns = getColumnsForDataRow(node);
        switch (node.data.type) {
            case 'header':
                result.push(compileHeaderRow(node, node.data, nodeColumns, theme));
                break;
            case 'section':
                result.push(...compileSectionRow(node, node.data, nodeColumns, theme, dataContext));
                break;
            case 'account':
                result.push(compileAccountRow(node, node.data, nodeColumns, theme, dataContext));
                break;
            case 'subtotal':
                result.push(compileSubtotalRow(node, node.data, nodeColumns, theme));
                break;
            case 'calculated':
                result.push(compileCalculatedRow(node, node.data, nodeColumns, theme));
                break;
            case 'spacer':
                result.push({
                    kind: 'spacer',
                    nodeId: node.id,
                    rowId: node.rowId,
                    depth: node.depth,
                    cells: [],
                });
                break;
            case 'line':
                result.push(compileLineRow(node, node.data));
                break;
            case 'grand_total':
                result.push(compileGrandTotalRow(node, node.data, nodeColumns, theme));
                break;
            default:
                break;
        }
    }
    return result;
}
// ============================================================
// Row compilers
// ============================================================
function compileTitleBlock(node, columns, _theme) {
    const data = node.data;
    const style = {
        bold: data.bold ?? false,
        italic: data.italic ?? false,
        fontSize: data.fontSize ?? 11,
        alignment: data.alignment,
    };
    // Title block uses the first column def (label) for its cell
    const labelCol = columns[0];
    const cell = {
        columnDef: labelCol ?? { id: '', header: '', type: 'label' },
        value: { kind: 'text', text: data.text },
        style,
    };
    return {
        kind: 'title_block',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells: [cell],
        spanFullWidth: true,
    };
}
function compileColumnHeaders(columns, theme) {
    const cells = columns.map((col) => ({
        columnDef: col,
        value: { kind: 'text', text: col.header },
        style: {
            bold: true,
            alignment: (col.type === 'label' || col.type === 'account_code' ? 'left' : 'center'),
            backgroundColor: theme.colors.headerBackground,
            textColor: '#FFFFFF',
        },
    }));
    return {
        kind: 'column_headers',
        nodeId: '__default_headers',
        rowId: '__default_headers',
        depth: 0,
        cells,
    };
}
function compileColumnHeadersFromNode(node, data, theme) {
    const cells = data.columns.map((col) => ({
        columnDef: col,
        value: { kind: 'text', text: col.header },
        style: {
            bold: true,
            alignment: (col.type === 'label' || col.type === 'account_code' ? 'left' : 'center'),
            backgroundColor: theme.colors.headerBackground,
            textColor: '#FFFFFF',
        },
    }));
    return {
        kind: 'column_headers',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells,
    };
}
function compileHeaderRow(node, data, columns, theme) {
    const fontSize = data.level === 1 ? 14 : data.level === 2 ? 12 : 11;
    const labelCol = columns[0];
    const cell = {
        columnDef: labelCol ?? { id: '', header: '', type: 'label' },
        value: { kind: 'text', text: data.text },
        style: {
            bold: true,
            fontSize,
            backgroundColor: theme.colors.headerBackground,
            textColor: '#FFFFFF',
        },
    };
    return {
        kind: 'header',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells: [cell],
        spanFullWidth: true,
    };
}
function compileSectionRow(node, data, columns, theme, dataContext) {
    const accounts = filterAccounts(data.filter, dataContext.accounts);
    const rows = [];
    const accountRowIds = [];
    for (const account of accounts) {
        const cells = buildAccountCells(account.code, account.name, columns, theme);
        const accountRowId = `${node.rowId}__acct_${account.code}`;
        accountRowIds.push(accountRowId);
        rows.push({
            kind: 'data',
            nodeId: node.id,
            rowId: accountRowId,
            depth: node.depth,
            cells,
        });
    }
    if (data.showSubtotal && accountRowIds.length > 0) {
        const label = data.subtotalLabel ?? 'Subtotal';
        const cells = buildSumOfCells(label, accountRowIds, columns, theme, 'subtotal');
        rows.push({
            kind: 'subtotal',
            nodeId: node.id,
            rowId: `${node.rowId}__subtotal`,
            depth: node.depth,
            cells,
        });
    }
    return rows;
}
function compileAccountRow(node, data, columns, theme, dataContext) {
    const account = dataContext.accounts.find((a) => a.code === data.accountCode);
    const displayName = data.label ?? account?.name ?? data.accountCode;
    const cells = buildAccountCells(data.accountCode, displayName, columns, theme);
    return {
        kind: 'data',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells,
    };
}
function compileSubtotalRow(node, data, columns, theme) {
    const cells = buildSumOfCells(data.label, data.sumOf, columns, theme, 'subtotal');
    applyRowStyleToCells(cells, data.style);
    return {
        kind: 'subtotal',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells,
    };
}
function compileCalculatedRow(node, data, columns, theme) {
    const parsed = data.formula ? parseFormula(data.formula) : undefined;
    const refRowIds = parsed?.references.map((ref) => ref.rowId) ?? [];
    const cells = columns.map((col) => {
        if (col.type === 'label') {
            return {
                columnDef: col,
                value: { kind: 'text', text: data.label },
            };
        }
        if (col.type === 'account_code') {
            return {
                columnDef: col,
                value: { kind: 'text', text: '' },
            };
        }
        const instruction = {
            type: 'row_arithmetic',
            rowRefs: refRowIds,
            userFormula: data.formula,
        };
        return {
            columnDef: col,
            value: { kind: 'formula', instruction },
            style: {
                numberFormat: col.format,
            },
        };
    });
    applyRowStyleToCells(cells, data.style);
    return {
        kind: 'calculated',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells,
    };
}
function compileGrandTotalRow(node, data, columns, theme) {
    const cells = columns.map((col) => {
        if (col.type === 'label') {
            return {
                columnDef: col,
                value: { kind: 'text', text: data.label },
                style: {
                    bold: true,
                    backgroundColor: theme.colors.headerBackground,
                    textColor: '#FFFFFF',
                    borderBottom: 'double',
                },
            };
        }
        if (col.type === 'account_code') {
            return {
                columnDef: col,
                value: { kind: 'text', text: '' },
                style: {
                    bold: true,
                    backgroundColor: theme.colors.headerBackground,
                    textColor: '#FFFFFF',
                    borderBottom: 'double',
                },
            };
        }
        const instruction = {
            type: 'sum_rows',
            rowRefs: data.sumOf,
        };
        return {
            columnDef: col,
            value: { kind: 'formula', instruction },
            style: {
                bold: true,
                backgroundColor: theme.colors.headerBackground,
                textColor: '#FFFFFF',
                borderBottom: 'double',
                numberFormat: col.format,
            },
        };
    });
    return {
        kind: 'grand_total',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells,
    };
}
function compileLineRow(node, data) {
    return {
        kind: 'line',
        nodeId: node.id,
        rowId: node.rowId,
        depth: node.depth,
        cells: [],
        rowHeight: 6,
        spanFullWidth: true,
    };
}
// ============================================================
// Cell builders
// ============================================================
/**
 * Build cells for an account data row.
 * Each non-label column gets a FormulaInstruction describing
 * what data to fetch (balance, budget, prior period, etc.).
 */
function buildAccountCells(accountCode, displayName, columns, theme) {
    return columns.map((col) => {
        if (col.type === 'label') {
            return {
                columnDef: col,
                value: { kind: 'text', text: displayName },
            };
        }
        if (col.type === 'account_code') {
            return {
                columnDef: col,
                value: { kind: 'text', text: accountCode },
            };
        }
        const instruction = buildFormulaInstructionForColumn(col, accountCode);
        if (!instruction) {
            return {
                columnDef: col,
                value: { kind: 'empty' },
            };
        }
        return {
            columnDef: col,
            value: { kind: 'formula', instruction },
            style: {
                numberFormat: col.format,
            },
        };
    });
}
/**
 * Build cells for a subtotal or grand_total style row that sums other rows.
 */
function buildSumOfCells(label, sumOfRowIds, columns, theme, rowKind) {
    const bgColor = rowKind === 'grand_total'
        ? theme.colors.headerBackground
        : theme.colors.subtotalBackground;
    return columns.map((col) => {
        if (col.type === 'label') {
            return {
                columnDef: col,
                value: { kind: 'text', text: label },
                style: {
                    bold: true,
                    backgroundColor: bgColor,
                },
            };
        }
        if (col.type === 'account_code') {
            return {
                columnDef: col,
                value: { kind: 'text', text: '' },
                style: {
                    bold: true,
                    backgroundColor: bgColor,
                },
            };
        }
        // Variance % should be left blank on subtotal/grand_total rows
        if (col.type === 'variance_percent') {
            return {
                columnDef: col,
                value: { kind: 'empty' },
                style: {
                    bold: true,
                    backgroundColor: bgColor,
                },
            };
        }
        const instruction = {
            type: 'sum_rows',
            rowRefs: sumOfRowIds,
        };
        return {
            columnDef: col,
            value: { kind: 'formula', instruction },
            style: {
                bold: true,
                backgroundColor: bgColor,
                numberFormat: col.format,
            },
        };
    });
}
/**
 * Maps a ColumnDefinition to the appropriate FormulaInstruction
 * for an account-level data cell.
 */
function buildFormulaInstructionForColumn(col, accountCode) {
    switch (col.type) {
        case 'label':
        case 'account_code':
            return null;
        case 'current':
            return { type: 'clm_balance', accountCode };
        case 'prior_period':
            return { type: 'clm_prior_period', accountCode };
        case 'prior_year':
            return { type: 'clm_prior_year', accountCode };
        case 'budget':
            return { type: 'clm_budget', accountCode, budgetName: col.budgetName };
        case 'ytd':
            return { type: 'clm_ytd', accountCode };
        case 'variance_amount':
            return { type: 'variance_amount', baseColumnId: col.baseColumnId, compareColumnId: col.compareColumnId };
        case 'variance_percent':
            return { type: 'variance_percent', baseColumnId: col.baseColumnId, compareColumnId: col.compareColumnId };
        case 'period_offset':
            return { type: 'clm_period_offset', accountCode, periodOffset: col.periodOffset };
        case 'year_offset':
            return { type: 'clm_year_offset', accountCode, periodOffset: col.periodOffset };
        case 'calculated':
            return col.formula
                ? { type: 'row_arithmetic', userFormula: col.formula }
                : null;
    }
}
// ============================================================
// Shared helpers
// ============================================================
function filterAccounts(filter, accounts) {
    let expandedCodes = filter.codes;
    if (filter.codes?.length) {
        const parentCodes = new Set(accounts.filter((a) => a.parent === null).map((a) => a.code));
        const expanded = new Set();
        for (const code of filter.codes) {
            if (parentCodes.has(code)) {
                for (const acct of accounts) {
                    if (acct.parent === code)
                        expanded.add(acct.code);
                }
            }
            else {
                expanded.add(code);
            }
        }
        expandedCodes = expanded.size > 0 ? [...expanded] : undefined;
    }
    return accounts.filter((account) => {
        if (account.parent === null)
            return false;
        if (filter.types?.length && !filter.types.includes(account.type))
            return false;
        if (filter.parentCode && account.parent !== filter.parentCode)
            return false;
        if (expandedCodes?.length && !expandedCodes.includes(account.code))
            return false;
        if (filter.excludeCodes?.length && filter.excludeCodes.includes(account.code))
            return false;
        return true;
    });
}
function applyRowStyleToCells(cells, style) {
    if (!style || style === 'normal')
        return;
    for (const cell of cells) {
        const existing = cell.style ?? {};
        if (style === 'bold') {
            cell.style = { ...existing, bold: true };
        }
        else if (style === 'underline') {
            cell.style = { ...existing, borderBottom: 'thin' };
        }
        else if (style === 'double_underline') {
            cell.style = { ...existing, borderBottom: 'double' };
        }
    }
}
//# sourceMappingURL=report-compiler.js.map